import React, { useEffect } from 'react';
import { logEvent } from '../../utils/telemetry';
import { ArrowLeft, AlertTriangle, Pill, CheckSquare, ExternalLink, AlertCircle, Activity, List, GitBranch, Phone, Clock } from 'lucide-react';
import CollapsibleSection from './CollapsibleSection';
import ProtocolCrossReference from './ProtocolCrossReference';

const ProtocolTemplate = ({ protocol, onClose, onNavigateToProtocol, breadcrumb }) => {
  useEffect(() => { logEvent('protocol', { protocolId: protocol.id, title: protocol.title, category: protocol.category }); }, [protocol.id, protocol.title, protocol.category]);
  if (!protocol.content) {
    // Coming Soon placeholder
    return (
      <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="bg-gradient-to-r from-gray-400 to-gray-500 text-white p-6 pb-8">
          <button onClick={() => { onClose(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
            <ArrowLeft size={20} /><span>Back</span>
          </button>
          <div className="flex items-center gap-4">
            <AlertCircle size={40} />
            <div className="flex-1">
              <span className="inline-block px-2 py-1 bg-white/20 rounded text-xs font-medium mb-2">
                {protocol.category}
              </span>
              <h1 className="text-2xl font-bold">{protocol.title}</h1>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <AlertTriangle size={48} className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">Protocol Coming Soon</h3>
            <p className="text-gray-600 dark:text-gray-400">
              This protocol is currently being developed and will be available in a future update.
            </p>
          </div>
        </div>
        <div className="h-8"></div>
      </div>
    );
  }

  // Helper to render cross-references in text
  const renderTextWithCrossRefs = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Look for patterns like "→ Protocol Name" or "go to Protocol Name"
    const crossRefPattern = /→\s*([A-Za-z\s\-/()]+?)(?=\.|$|,)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = crossRefPattern.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add cross-reference component
      const protocolName = match[1].trim();
      parts.push(
        <ProtocolCrossReference
          key={match.index}
          protocolId={protocolName.toLowerCase().replace(/\s+/g, '-')}
          protocolTitle={protocolName}
          onNavigate={onNavigateToProtocol || (() => {})}
        />
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen pb-20">
      {/* Sticky Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 pb-6 sticky top-0 z-20 shadow-lg">
        <button
          onClick={() => { onClose(); }}
          className="flex items-center gap-2 text-white mb-3 hover:opacity-80 active:scale-95 transition-all"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">Back</span>
        </button>

        {breadcrumb && (
          <div className="text-xs text-red-100 mb-2 flex items-center gap-1">
            <span>From:</span>
            <span className="font-semibold">{breadcrumb}</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          <AlertCircle size={36} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="inline-block px-2 py-0.5 bg-white/20 rounded text-xs font-medium mb-1">
              {protocol.category}
            </span>
            <h1 className="text-xl font-bold leading-tight">{protocol.title}</h1>
            {protocol.subtitle && (
              <p className="text-red-100 text-sm mt-0.5">{protocol.subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {/* Protocol Content */}
      <div className="p-4 space-y-4">

        {/* IMMEDIATE ACTIONS - Always expanded, never collapsible */}
        {protocol.content.immediate && (
          <CollapsibleSection
              title="IMMEDIATE ACTIONS"
              icon={AlertTriangle}
              colorScheme="red"
              alwaysOpen={true}
            >
              <ul className="space-y-2.5 mt-3">
                {protocol.content.immediate.map((action, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-red-600 dark:text-red-400 font-bold text-lg flex-shrink-0 mt-0.5">
                      {idx + 1}.
                    </span>
                    <span className="text-red-900 dark:text-red-100 font-semibold text-base leading-relaxed">
                      {renderTextWithCrossRefs(action)}
                    </span>
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
        )}

        {/* EMERGENCY CONTACT - Prominent phone link */}
        {protocol.content.emergencyContact && (
          <a
            href={`tel:${protocol.content.emergencyContact.number.replace(/\D/g, '')}`}
            onClick={() => {}}
            className="block bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 active:scale-[0.98] text-white rounded-xl p-4 shadow-lg transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-3">
                <Phone size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-red-100 uppercase tracking-wide mb-0.5">
                  {protocol.content.emergencyContact.name}
                </div>
                <div className="text-2xl font-bold tracking-wide">
                  {protocol.content.emergencyContact.number}
                </div>
                <div className="text-xs text-red-100 mt-0.5">
                  {protocol.content.emergencyContact.description}
                </div>
              </div>
              <Phone size={20} className="text-white/60" />
            </div>
          </a>
        )}

        {/* RECOGNITION / DIAGNOSIS - Default open */}
        {protocol.content.recognition && (
          <CollapsibleSection
            title="RECOGNITION / DIAGNOSIS"
            icon={CheckSquare}
            colorScheme="gray"
            defaultOpen={true}
          >
            <ul className="space-y-2 mt-3">
              {protocol.content.recognition.map((sign, idx) => {
                // Check if this item is a header (ends with ":")
                const isHeader = typeof sign === 'string' && sign.trim().endsWith(':');

                if (isHeader) {
                  // Render as header without checkbox
                  return (
                    <li key={idx} className="mt-3 first:mt-0">
                      <span className="text-gray-900 dark:text-white font-bold leading-relaxed">
                        {renderTextWithCrossRefs(sign)}
                      </span>
                    </li>
                  );
                }

                // Determine if this is a sub-item by checking if any previous item is a header
                // Simple rule: items after a header are sub-items until the end (or next header)
                let isSubItem = false;
                for (let i = 0; i < idx; i++) {
                  if (typeof protocol.content.recognition[i] === 'string' &&
                      protocol.content.recognition[i].trim().endsWith(':')) {
                    isSubItem = true;
                    // Keep checking in case there's a newer header that resets the sub-list
                  }
                }

                if (isSubItem) {
                  // Render as indented sub-item with bullet
                  return (
                    <li key={idx} className="flex items-start gap-2.5 ml-6">
                      <span className="text-gray-400 dark:text-gray-500 text-base">•</span>
                      <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm">
                        {renderTextWithCrossRefs(sign)}
                      </span>
                    </li>
                  );
                } else {
                  // Render as regular item with checkbox
                  return (
                    <li key={idx} className="flex items-start gap-2.5">
                      <span className="text-gray-400 dark:text-gray-500 text-lg">□</span>
                      <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {renderTextWithCrossRefs(sign)}
                      </span>
                    </li>
                  );
                }
              })}
            </ul>
          </CollapsibleSection>
        )}

        {/* TREATMENT STEPS - Default open */}
        {protocol.content.treatment && (
          <CollapsibleSection
            title="TREATMENT STEPS"
            icon={List}
            colorScheme="gray"
            defaultOpen={true}
          >
            <ol className="space-y-3 mt-3">
              {protocol.content.treatment.map((step, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="font-bold text-gray-700 dark:text-gray-300 flex-shrink-0 text-lg">
                    {idx + 1}.
                  </span>
                  <div className="flex-1">
                    <p className="text-gray-900 dark:text-white font-medium leading-relaxed">
                      {renderTextWithCrossRefs(step.action || step)}
                    </p>
                    {step.details && (
                      <ul className="mt-2 ml-4 space-y-1.5">
                        {step.details.map((detail, dIdx) => (
                          <li key={dIdx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                            <span className="text-gray-400 dark:text-gray-500">•</span>
                            <span>{renderTextWithCrossRefs(detail)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </CollapsibleSection>
        )}

        {/* DRUG DOSING - Default open */}
        {protocol.content.drugs && (
          <CollapsibleSection
            title="DRUG DOSING"
            icon={Pill}
            colorScheme="blue"
            defaultOpen={true}
          >
            <div className="space-y-2.5 mt-3">
              {protocol.content.drugs.map((drug, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                  <p className="font-bold text-gray-900 dark:text-white text-base">
                    {drug.name}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">
                    {drug.dose}
                  </p>
                  {drug.notes && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 italic mt-1.5">
                      {drug.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* DIFFERENTIAL DIAGNOSIS - Default closed */}
        {protocol.content.differential && (
          <CollapsibleSection
            title="DIFFERENTIAL DIAGNOSIS"
            icon={GitBranch}
            colorScheme="purple"
            defaultOpen={false}
          >
            <ul className="space-y-2 mt-3">
              {protocol.content.differential.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <span className="text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5">•</span>
                  <span className="text-purple-900 dark:text-purple-100 leading-relaxed">
                    {renderTextWithCrossRefs(item)}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {/* CRITICAL CHANGES - Default open */}
        {protocol.content.criticalChanges && (
          <CollapsibleSection
            title="CRITICAL CHANGES"
            icon={Activity}
            colorScheme="orange"
            defaultOpen={true}
          >
            <div className="space-y-2.5 mt-3">
              {protocol.content.criticalChanges.map((change, idx) => (
                <div key={idx} className="text-orange-900 dark:text-orange-100">
                  <p className="font-semibold mb-1">{change.condition}</p>
                  <div className="ml-3">
                    {renderTextWithCrossRefs(change.action)}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* IMPORTANT NOTES - Default closed */}
        {protocol.content.notes && (
          <CollapsibleSection
            title="IMPORTANT NOTES"
            icon={AlertCircle}
            colorScheme="amber"
            defaultOpen={false}
          >
            <ul className="space-y-2 mt-3">
              {protocol.content.notes.map((note, idx) => (
                <li key={idx} className="text-sm text-amber-900 dark:text-amber-100 flex items-start gap-2 leading-relaxed">
                  <span className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5">•</span>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {/* POST-ACUTE CARE - Default closed */}
        {protocol.content.postAcuteCare && (
          <CollapsibleSection
            title="POST-ACUTE CARE"
            icon={Clock}
            colorScheme="gray"
            defaultOpen={false}
          >
            <div className="space-y-3 mt-3">
              {protocol.content.postAcuteCare.map((item, idx) => (
                <div key={idx} className="border-l-4 border-teal-500 dark:border-teal-400 pl-3">
                  <p className="font-bold text-gray-900 dark:text-white text-sm mb-1">
                    {item.title}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {item.content}
                  </p>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* ADDITIONAL READING - Always visible at bottom */}
        {protocol.content.references && protocol.content.references.length > 0 && (
          <div className="bg-teal-50 dark:bg-teal-900/20 border-2 border-teal-300 dark:border-teal-600 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <ExternalLink size={20} className="text-teal-600 dark:text-teal-400" />
              <h3 className="font-bold text-teal-900 dark:text-teal-100 text-lg">ADDITIONAL READING</h3>
            </div>
            <p className="text-xs text-teal-800 dark:text-teal-200 mb-3 italic">
              For debriefing and further learning
            </p>
            <div className="space-y-2">
              {protocol.content.references.map((ref, idx) => (
                <a
                  key={idx}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {}}
                  className="flex items-start gap-2.5 p-3 bg-white dark:bg-gray-800 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/30 active:scale-[0.98] transition-all group border border-teal-200 dark:border-teal-700"
                >
                  <ExternalLink size={16} className="text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-teal-900 dark:group-hover:text-teal-100 leading-snug">
                      {ref.title}
                    </p>
                    {ref.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                        {ref.description}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* AUTHORS - Attribution at bottom */}
        {protocol.content.authors && protocol.content.authors.length > 0 && (
          <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Protocol Authors
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              {protocol.content.authors.join(', ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProtocolTemplate;
